import { useState, useEffect } from 'react'
import { isHTTPError } from 'ky'
import { format } from 'date-fns'
import { UTCDate } from '@date-fns/utc'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, Key } from 'lucide-react'
import { api } from '@frontend/lib/api'
import type { ClientListItem, ClientResponse } from '@common/types'
import type { UpdateClientKeyInput } from '@common/schemas'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface UpdateKeyModalProps {
  client: ClientListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

async function updateClientKey(clientId: string, data: UpdateClientKeyInput): Promise<ClientResponse> {
  return api.put(`clients/${clientId}/key`, { json: data }).json<ClientResponse>()
}

function formatDateForInput(dateString: string | null): string {
  if (!dateString) return ''
  // Parse as UTC to avoid timezone shift, format as yyyy-MM-dd for HTML date input
  const date = new UTCDate(dateString)
  return format(date, 'yyyy-MM-dd')
}

export function UpdateKeyModal({ client, open, onOpenChange }: UpdateKeyModalProps) {
  const queryClient = useQueryClient()

  // Form state - prepopulate keyExpiration if client has key
  const [apiKey, setApiKey] = useState('')
  const [keyExpiration, setKeyExpiration] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Reset form when modal opens, prepopulate expiration if client has existing key
  useEffect(() => {
    if (open && client) {
      setApiKey('')
      setKeyExpiration(client.hasKey ? formatDateForInput(client.keyExpiration) : '')
      setError(null)
    }
  }, [open, client])

  const updateMutation = useMutation({
    mutationFn: (data: UpdateClientKeyInput) => updateClientKey(client!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients-detailed'] })
      onOpenChange(false)
      toast.success(client?.hasKey ? `API key updated for "${client.name}"` : `API key added for "${client!.name}"`)
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to update API key')
      } else {
        setError('Failed to update API key')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!client) return

    if (!apiKey.trim()) {
      setError('API key is required')
      return
    }

    const data: UpdateClientKeyInput = {
      apiKey: apiKey.trim(),
      keyExpiration: keyExpiration || null,
    }

    updateMutation.mutate(data)
  }

  const isSubmitting = updateMutation.isPending
  const isAddingKey = !client?.hasKey

  if (!client) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            {isAddingKey ? 'Add API Key' : 'Update API Key'}
          </DialogTitle>
          <DialogDescription>
            {isAddingKey ? `Add a Drata API key for ${client.name}` : `Update the Drata API key for ${client.name}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* API Key */}
          <div>
            <label htmlFor="apiKey" className="block text-sm font-medium mb-1">
              Drata API Key <span className="text-red-500">*</span>
            </label>
            <input
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter API key"
              disabled={isSubmitting}
              autoComplete="off"
              data-1p-ignore
            />
            {!isAddingKey && (
              <p className="text-xs text-muted-foreground mt-1">Enter the new API key to replace the existing one</p>
            )}
          </div>

          {/* Key Expiration */}
          <div>
            <label htmlFor="keyExpiration" className="block text-sm font-medium mb-1">
              API Key Expiration Date
            </label>
            <input
              id="keyExpiration"
              type="date"
              value={keyExpiration}
              onChange={(e) => setKeyExpiration(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground mt-1">Optional - when does this key expire?</p>
          </div>

          {/* Error display */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : isAddingKey ? (
                'Add Key'
              ) : (
                'Update Key'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
